import { useState, type ReactNode } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { Lock, Mail, ShieldAlert } from 'lucide-react'
import { Wordmark } from './components/Logo'
import { Callout, Spinner } from './components/ui'
import { usePortal } from './lib/portal-context'
import { CaseReport } from './pages/CaseReport'
import { Dashboard } from './pages/Dashboard'
import { HelpPage } from './pages/Help'
import { NewCase } from './pages/NewCase'
import { SettingsPage } from './pages/Settings'
import { UsersPage } from './pages/Users'

export default function App() {
  const { loading, error, config } = usePortal()

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4">
        <Wordmark height={26} />
        <span className="flex items-center gap-2 text-[13px] text-slate-500">
          <Spinner /> Starting the portal…
        </span>
      </div>
    )
  }

  if (error || !config) {
    return (
      <div className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center px-6">
        <Wordmark height={24} />
        <Callout tone="danger" title="Cannot reach the portal server" className="mt-6">
          {error ?? 'Unknown error.'}
          <p className="mt-2">
            Start it with <code className="rounded bg-black/40 px-1">npm run dev</code> (development)
            or <code className="rounded bg-black/40 px-1">npm start</code> (production).
          </p>
        </Callout>
      </div>
    )
  }

  return (
    <Routes>
      {/* Public: the link that goes out in email */}
      <Route path="/case/:caseNumber" element={<CaseReport />} />

      <Route
        path="/"
        element={
          <RequireAuth>
            <Dashboard />
          </RequireAuth>
        }
      />
      <Route
        path="/new"
        element={
          <RequireAuth permission="cases:write">
            <NewCase />
          </RequireAuth>
        }
      />
      <Route
        path="/users"
        element={
          <RequireAuth permission="users:manage">
            <UsersPage />
          </RequireAuth>
        }
      />
      <Route
        path="/settings"
        element={
          <RequireAuth>
            <SettingsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/help"
        element={
          <RequireAuth>
            <HelpPage />
          </RequireAuth>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

function RequireAuth({
  children,
  permission,
}: {
  children: ReactNode
  permission?: string
}) {
  const { user, can } = usePortal()
  if (!user) return <LoginScreen />
  if (permission && !can(permission)) {
    return (
      <div className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center px-6">
        <Wordmark height={24} />
        <Callout tone="warn" title="You do not have access" className="mt-6">
          Your account ({user.role}) cannot open this page. Ask an admin if you need a different
          permission level.
        </Callout>
      </div>
    )
  }
  return <>{children}</>
}

function LoginScreen() {
  const { login, config } = usePortal()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [working, setWorking] = useState(false)

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setWorking(true)
    setError(null)
    try {
      await login(email.trim(), password)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Sign-in failed.')
      setPassword('')
    } finally {
      setWorking(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4 grid-bg">
      <div className="panel w-full max-w-sm px-6 py-7">
        <Wordmark height={22} />
        <h1 className="mt-6 text-[19px] font-semibold tracking-[-0.01em] text-white">
          Sign in
        </h1>
        <p className="mt-1.5 text-[13px] leading-relaxed text-slate-400">
          Use your portal account to file and manage violation reports for {config?.community}.
        </p>

        <form onSubmit={submit} className="mt-5 space-y-3">
          <div className="relative">
            <Mail
              size={15}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"
            />
            <input
              type="email"
              autoComplete="username"
              autoFocus
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="Email"
              className="input pl-9"
            />
          </div>
          <div className="relative">
            <Lock
              size={15}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"
            />
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="Password"
              className="input pl-9"
            />
          </div>
          {error ? (
            <p className="flex items-center gap-1.5 text-[12.5px] text-red-300">
              <ShieldAlert size={14} />
              {error}
            </p>
          ) : null}
          <button
            type="submit"
            disabled={!email || !password || working}
            className="btn-primary w-full"
          >
            {working ? <Spinner /> : null}
            Sign in
          </button>
        </form>

        <p className="mt-5 text-[11.5px] leading-relaxed text-slate-600">
          Case report links shared by email stay viewable without signing in, so HOA staff can open
          them directly.
        </p>
      </div>
    </div>
  )
}
